import React, { useEffect } from "react";
import { useRef } from "react";
import { useState } from "react";
import styled, { css } from "styled-components";
import PropTypes from "prop-types";

export function Carousel({ children, edges, gap, dir, onView }) {
  const [touchOffset, setTouchOffset] = useState(0);
  const [index, setIndex] = useState(0);
  const TouchHelperRef = useRef(null);

  const hendelTouchStart = (e) => {
    const x = e.changedTouches[0].clientX;
    TouchHelperRef.current = x;
  };

  useEffect(() => {
    if (onView) onView(children[index], index);
  }, [children, index, onView]);

  const hendelTouchMove = (e) => {
    const x = e.changedTouches[0].clientX;
    const delta = TouchHelperRef.current - x;
    setTouchOffset((prev) => prev - delta);
    TouchHelperRef.current = x;
  };

  const hendelTouchEnd = () => {
    const sign = dir === "rtl" ? -1 : 1;
    if (touchOffset * sign > 0) setIndex((prev) => Math.max(prev - 1, 0));
    if (touchOffset * sign < 0)
      setIndex((prev) => Math.min(prev + 1, children.length - 1));
    setTouchOffset(0);
    TouchHelperRef.current = null;
  };

  return (
    <CarouselWrapper
      onTouchStart={hendelTouchStart}
      onTouchMoveCapture={hendelTouchMove}
      onTouchEnd={hendelTouchEnd}
      dir={dir}
    >
      <InnerCarouselWrapper
        style={{
          "--edges": edges,
          "--gap": gap,
          "--sign": dir === "rtl" ? -1 : 1,
          transform: `translateX(calc( var(--sign) * (var(--box-width) * ${index} + var(--edges)) + ${touchOffset}px))`,
          transition: touchOffset ? "none" : "transform 200ms linear",
        }}
      >
        {children.map((child, i) => (
          <Box key={i} inView={i === index}>
            {child}
          </Box>
        ))}
      </InnerCarouselWrapper>
      <ControlDots>
        {children.map((_, i) => (
          <Dot key={i} selected={index === i} />
        ))}
      </ControlDots>
    </CarouselWrapper>
  );
}

Carousel.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node),
  edges: PropTypes.string,
  gap: PropTypes.string,
  onView: PropTypes.func,
  dir: PropTypes.oneOf(["rtl", "ltr"]),
};

Carousel.defaultProps = {
  edges: "30px",
  gap: "0px",
  dir: "ltr",
};

const CarouselWrapper = styled.div`
  overflow: hidden;
`;

const InnerCarouselWrapper = styled.div`
  display: flex;
  gap: var(--gap);
  --box-width: calc(-100% + var(--edges) * 2 - var(--gap));
`;

const ControlDots = styled.ul`
  bottom: 0;
  margin-top: 30px;
  padding: 0;
  text-align: center;
  width: 100%;
  z-index: 1;
`;

const Dot = styled.li`
  transition: opacity 0.25s ease-in;
  opacity: 0.4;
  background: #84a4fb;
  border-radius: 50%;
  width: 8px;
  height: 8px;
  display: inline-block;
  margin: 0 8px;
  ${({ selected }) =>
    selected &&
    css`
      opacity: 1;
    `}
`;

const Box = styled.div`
  min-width: calc(100% - var(--edges) * 2);
  transition: scale 200ms linear;
  scale: ${({ inView }) => (inView ? 1 : 0.9)};
`;
